import { useTranslation } from 'react-i18next'

import { RENTAL_PORTAL_LISTING_STATUSES } from '../../../../../constants/rentalPortal'
import Button from '../../../../Button'
import { Checkbox, CHECKBOX_SIZE, CHECKBOX_TYPE } from '../../../../Checkbox'
import { Counter, TABLE_VARIANT } from '../../../../Counter'
import { EntityStatus } from '../../../../Entity'
import { Skeleton } from '../../../../Skeleton'
import Text from '../../../../Text'
import { getRentalPortalBoardSelectionState } from '../../../RentalPortal.utils'
import {
  QA_NAME_LISTING_BOARD_COLUMN_SELECT,
  QA_NAME_LISTING_BOARD_MORE,
} from '../RentalPortalListingsBoard.constants'
import { getListingsBoardColumnItems } from '../RentalPortalListingsBoard.utils'
import { RentalPortalListingsBoardCard } from '../RentalPortalListingsBoardCard'
import { RentalPortalListingsBoardSkeleton } from '../RentalPortalListingsBoardSkeleton'

import * as styles from './RentalPortalListingsBoardColumn.module.css'

function RentalPortalListingsBoardColumn({
  activeItem,
  getActions,
  getIsItemSelectable,
  hasSelection,
  isLoading,
  items,
  selectedIds,
  selectNotAllowedTooltip,
  status,
  total,
  onCallAction,
  onClickCard,
  onSelectCard,
  onSelectColumn,
  onShowMore,
}) {
  const { t } = useTranslation()

  const { hiddenCount, visibleItems } = getListingsBoardColumnItems({
    items,
    total,
  })

  const { isAllSelected, isPartlySelected, selectableCount, selectableItems } =
    getRentalPortalBoardSelectionState({
      getIsSelectable: getIsItemSelectable,
      items,
      selectedIds,
    })

  const columnLabel = t(RENTAL_PORTAL_LISTING_STATUSES[status].text)

  const selectAllLabel = t('RentalPortal.BoardColumnSelectAllKey', {
    column: columnLabel,
  })

  function handleClickMore() {
    onShowMore?.(status)
  }

  function handleSelectColumn(isChecked) {
    onSelectColumn(selectableItems, isChecked)
  }

  return (
    <div className={styles.RentalPortalListingsBoardColumn}>
      <div className={styles.header}>
        {hasSelection ? (
          <Checkbox
            checked={isAllSelected}
            disabled={isLoading || selectableCount === 0}
            label={selectAllLabel}
            labelClassName={styles.selectLabel}
            qaName={QA_NAME_LISTING_BOARD_COLUMN_SELECT}
            selected={isPartlySelected}
            size={CHECKBOX_SIZE.small}
            tooltip={
              selectableCount === 0
                ? t('RentalPortal.BoardColumnSelectNoneAllowedKey', {
                    column: columnLabel,
                  })
                : selectAllLabel
            }
            type={CHECKBOX_TYPE.table}
            onChange={handleSelectColumn}
          />
        ) : null}
        <EntityStatus id={status} statuses={RENTAL_PORTAL_LISTING_STATUSES} />
        {isLoading ? (
          <Skeleton className={styles.skeletonCount} />
        ) : total > 0 ? (
          <Counter count={total} variant={TABLE_VARIANT} />
        ) : (
          <Text className={styles.zeroCount}>{total}</Text>
        )}
      </div>
      <div className={styles.track}>
        {isLoading ? (
          <RentalPortalListingsBoardSkeleton />
        ) : items.length > 0 ? (
          visibleItems.map(item => (
            <RentalPortalListingsBoardCard
              getActions={getActions}
              isActive={item.id === activeItem}
              isSelectable={getIsItemSelectable(item)}
              isSelected={selectedIds.includes(item.id)}
              item={item}
              key={item.id}
              selectNotAllowedTooltip={selectNotAllowedTooltip}
              onCallAction={onCallAction}
              onClick={onClickCard}
              onSelect={hasSelection ? onSelectCard : undefined}
            />
          ))
        ) : (
          <Text isSecondary className={styles.empty}>
            {t('RentalPortal.ListingBoardEmptyColumnKey')}
          </Text>
        )}
      </div>
      {hiddenCount > 0 ? (
        <Button.Text
          className={styles.moreButton}
          qaName={QA_NAME_LISTING_BOARD_MORE}
          text={t('RentalPortal.ListingBoardMoreCountKey', {
            count: hiddenCount,
          })}
          onClick={handleClickMore}
        />
      ) : null}
    </div>
  )
}

export default RentalPortalListingsBoardColumn
